import { Box, Fade, Typography } from "@mui/material";
import React from "react";
import LucideIcon from "../../../../components/LucideIcon";
import StepNavButtons from "./StepNavButtons";
import { useWhitelabel } from "../../../../context/WhitelabelContext";

interface RecapStepProps {
  appName: string;
  companyName: string;
  hasIcp: boolean;
  hasLeadList: boolean;
  hasSequence: boolean;
  hasContent: boolean;
  hasSamples: boolean;
  hasAgents: boolean;
  hasWidgets: boolean;
  onNext: () => void;
  onBack: () => void;
  // True while finishing persists onboarding completion before navigating away.
  finishing?: boolean;
}

export default function RecapStep({
  appName,
  companyName,
  hasIcp,
  hasLeadList,
  hasSequence,
  hasContent,
  hasSamples,
  hasAgents,
  hasWidgets,
  onNext,
  onBack,
  finishing = false,
}: RecapStepProps) {
  const { whitelabelSettings } = useWhitelabel();

  // Whitelabels can override the completion screen with their own markdown (same as the classic
  // CompletionStep). When set, render it instead of the built-in recap, substituting the same
  // {{appName}}/{{companyName}} placeholders. We keep StepNavButtons so finishing the flow stays
  // consistent with every other guided step.
  const customContent = whitelabelSettings?.onboardingCompletionContent;
  if (customContent) {
    const processed = customContent
      .replace(/\{\{appName\}\}/g, appName)
      .replace(/\{\{companyName\}\}/g, companyName || "");
    return (
      <Fade in timeout={600}>
        <Box>
          <Box dangerouslySetInnerHTML={{ __html: processed }} />
          <StepNavButtons onBack={onBack} onNext={onNext} nextLoading={finishing} />
        </Box>
      </Fade>
    );
  }

  const items = [
    {
      show: hasIcp,
      icon: "Target",
      title: "A targeted lead-generation profile",
      detail: "We mapped exactly who you should be selling to",
    },
    {
      show: hasLeadList,
      icon: "Users",
      title: "A starter list of real leads",
      detail: "In-market buyers matching your ideal customer, ready to work",
    },
    {
      show: hasSequence,
      icon: "Mail",
      title: "A ready-to-send sales sequence",
      detail: "A 5-email outreach cadence personalized to your ideal customer",
    },
    {
      show: hasContent,
      icon: "PenLine",
      title: "A full-funnel content plan",
      detail: "Content pillars across awareness, consideration, and decision",
    },
    {
      show: hasSamples,
      icon: "LayoutGrid",
      title: "Ready-to-publish sample content",
      detail: "A blog post, LinkedIn post, and Instagram post — each with an on-brand image",
    },
    {
      show: hasAgents,
      icon: "Users",
      title: "A team of AI agents",
      detail: "Ready to execute your strategy on day one",
    },
    {
      show: hasWidgets,
      icon: "Globe",
      title: "Website chat agents, ready to embed",
      detail: "Sales and support agents you can drop onto your site with one snippet",
    },
  ].filter((i) => i.show);

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          You've seen what {appName} can do
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          In just a few minutes, we built a personalized workspace for your business:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2.5,
                borderRadius: 2,
                backgroundColor: "action.hover",
              }}
            >
              <Box sx={{ color: "primary.main", flexShrink: 0 }}>
                <LucideIcon name={item.icon} size={24} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.detail}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
